"use client";

import { useEffect } from "react";
import { logger, toError } from "@/utils/logger";

/**
 * Global error handler component that catches unhandled errors and promise rejections
 * This prevents errors from appearing in the browser console and affecting Lighthouse scores
 */
export default function GlobalErrorHandler() {
  useEffect(() => {
    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Ignore NEXT_REDIRECT errors - these are expected when Next.js redirect() is called
      // in server actions and should not be logged as errors
      if (
        event.reason instanceof Error &&
        event.reason.message === "NEXT_REDIRECT"
      ) {
        // This is expected behavior - prevent default but don't log
        event.preventDefault();
        return;
      }

      // Prevent default browser console error
      event.preventDefault();

      // Log the error using our logger
      logger.error(
        "Unhandled promise rejection",
        { component: "GlobalErrorHandler" },
        toError(event.reason)
      );
    };

    // Handle general errors
    const handleError = (event: ErrorEvent) => {
      // Log the error using our logger
      // Note: We can't prevent default console logging, but we can ensure
      // all errors are also logged through our logger for consistency
      logger.error(
        "Unhandled error",
        {
          component: "GlobalErrorHandler",
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
        toError(event.error || new Error(event.message))
      );

      // Don't prevent default - let browser handle it, but we've logged it
      return false;
    };

    // Add event listeners
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("error", handleError);

    // Cleanup
    return () => {
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
      window.removeEventListener("error", handleError);
    };
  }, []);

  // This component doesn't render anything
  return null;
}
